if (!customElements.get('mobile-sticky-add-to-cart')) {
  customElements.define(
    'mobile-sticky-add-to-cart',
    class MobileStickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true' || this.closest('quick-add-modal')) return;

        this.productSection = this.closest('[id^="MainProduct-"]');
        this.sourceButton = this.productSection?.querySelector(
          'form[action*="/cart/add"] .product-form__submit[name="add"]'
        );
        this.proxyButton = this.querySelector('[data-sticky-add-to-cart-button]');
        this.proxyLabel = this.querySelector('[data-sticky-add-to-cart-label]');
        this.proxySpinner = this.querySelector('[data-sticky-add-to-cart-spinner]');
        this.sesamiContainer = this.querySelector('[data-sticky-sesami-container]');

        if (!this.productSection || !this.sourceButton || !this.proxyButton) return;

        this.dataset.initialized = 'true';
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.handleProxyClick = this.handleProxyClick.bind(this);
        this.updateVisibility = this.updateVisibility.bind(this);
        this.syncButtonState = this.syncButtonState.bind(this);
        this.updateStickyHeight = this.updateStickyHeight.bind(this);

        this.proxyButton.addEventListener('click', this.handleProxyClick);

        this.buttonObserver = new MutationObserver(this.syncButtonState);
        this.buttonObserver.observe(this.sourceButton, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'class', 'disabled'],
          characterData: true,
          childList: true,
          subtree: true
        });

        this.initializeSesamiExperience();
        this.sesamiObserver = new MutationObserver(() => this.initializeSesamiExperience());
        this.sesamiObserver.observe(this.productSection, {
          childList: true,
          subtree: true
        });

        this.resizeObserver = new ResizeObserver(this.updateStickyHeight);
        this.resizeObserver.observe(this);

        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this.updateVisibility);
        } else {
          this.mobileMediaQuery.addListener(this.updateVisibility);
        }

        this.syncButtonState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.proxyButton?.removeEventListener('click', this.handleProxyClick);
        this.buttonObserver?.disconnect();
        this.sesamiObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.restoreSesamiExperience();
        document.documentElement.classList.remove('mobile-sticky-add-to-cart-enabled');
        document.documentElement.style.removeProperty('--mobile-sticky-add-to-cart-height');

        if (!this.mobileMediaQuery) return;

        if (this.mobileMediaQuery.removeEventListener) {
          this.mobileMediaQuery.removeEventListener('change', this.updateVisibility);
        } else {
          this.mobileMediaQuery.removeListener(this.updateVisibility);
        }
      }

      handleProxyClick() {
        if (this.proxyButton.disabled || this.sourceButton.disabled) return;

        this.proxyButton.disabled = true;
        this.sourceButton.click();
        queueMicrotask(this.syncButtonState);
      }

      syncButtonState() {
        const sourceLabel = this.sourceButton.querySelector('span')?.textContent.trim();
        const isLoading = this.sourceButton.classList.contains('loading');
        const isDisabled =
          this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';

        if (sourceLabel && this.proxyLabel) this.proxyLabel.textContent = sourceLabel;

        this.proxyButton.disabled = isDisabled;
        this.proxyButton.classList.toggle('loading', isLoading);
        this.proxyLabel?.classList.toggle('hidden', isLoading);
        this.proxySpinner?.classList.toggle('hidden', !isLoading);

        if (this.sourceButton.hasAttribute('aria-haspopup')) {
          this.proxyButton.setAttribute('aria-haspopup', this.sourceButton.getAttribute('aria-haspopup'));
        } else {
          this.proxyButton.removeAttribute('aria-haspopup');
        }
      }

      initializeSesamiExperience() {
        if (this.sesamiExperience || !this.sesamiContainer) return;

        const experience = this.productSection.querySelector('sesami-experience');
        if (!experience || this.contains(experience)) return;

        this.sesamiExperience = experience;
        this.sesamiPlaceholder = document.createComment('sesami-sticky-placeholder');
        experience.before(this.sesamiPlaceholder);
        this.updateSesamiPosition();
      }

      updateSesamiPosition() {
        if (!this.sesamiExperience || !this.sesamiPlaceholder) return;

        if (this.mobileMediaQuery.matches) {
          if (this.sesamiExperience.parentNode !== this.sesamiContainer) {
            this.sesamiContainer.appendChild(this.sesamiExperience);
          }
          this.sesamiExperience.setAttribute('data-sticky-sesami', '');
          this.classList.add('mobile-sticky-add-to-cart--with-booking');
        } else {
          this.restoreSesamiExperience();
        }

        requestAnimationFrame(this.updateStickyHeight);
      }

      restoreSesamiExperience() {
        if (!this.sesamiExperience || !this.sesamiPlaceholder?.parentNode) return;

        this.sesamiPlaceholder.after(this.sesamiExperience);
        this.sesamiExperience.removeAttribute('data-sticky-sesami');
        this.classList.remove('mobile-sticky-add-to-cart--with-booking');
      }

      updateStickyHeight() {
        if (!this.mobileMediaQuery?.matches || this.hidden) {
          document.documentElement.style.removeProperty('--mobile-sticky-add-to-cart-height');
          return;
        }

        document.documentElement.style.setProperty(
          '--mobile-sticky-add-to-cart-height',
          `${this.getBoundingClientRect().height}px`
        );
      }

      updateVisibility() {
        const shouldShow = this.mobileMediaQuery.matches;

        this.hidden = !shouldShow;
        this.updateSesamiPosition();
        document.documentElement.classList.toggle('mobile-sticky-add-to-cart-enabled', shouldShow);
        requestAnimationFrame(this.updateStickyHeight);
      }
    }
  );
}
